import cn from 'classnames';
import { ProfileName } from 'components';
import { syncGridGroupsState } from 'components/Grid/utils';
import { COMMUNITY_ROUTES, ROUTES } from 'configs';
import { assoc, filter, includes, reduce } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import {
  IconSendMessage,
  StyledLink,
  StyledMessageBlock,
  StyledSendMessageLabel,
} from 'sidebars/ViewMember/styled';
import { Down } from 'static';
import { openModal } from 'store/actionCreators/modal';
import { getAuthUser } from 'store/selectors/auth';
import { getProfileName } from 'utils';

import { VIEW_MEMBER } from '../../../constants/sidebars';
import {
  ChatBtnWrapper,
  StyledAvatarNoPhoto,
  StyledGroupItem,
  StyledListItem,
  StyledNameWrapper,
} from './styled';

export const useRoles = (t, overallScheduler) => {
  const getGroupsData = (overallScheduler) => {
    const groups = Array.isArray(overallScheduler?.groups)
      ? overallScheduler.groups
      : [];
    const groupsData = groups
      .filter((group) => group.users)
      .map((group) => [
        {
          type: 'group',
          id: group.id,
          label: group.name,
        },
        ...group.users.map((member) => ({
          type: 'user',
          id: member.profile.userId,
          group: group.id,
          permission: {
            groupPermission: member?.groupPermission,
            communityPermission: overallScheduler.users.find(
              (u) => u.id === member.id,
            )?.communityPermission,
          },
          icon: member.profile.smallLogo || member.profile.logo || (
            <StyledAvatarNoPhoto>
              {getProfileName(member.profile)[0].toUpperCase()}
            </StyledAvatarNoPhoto>
          ),
          firstName: member.profile.firstName,
          lastName: member.profile.lastName,
          profileName: member.profile.profileName,
          email: member.profile.email,
          user: member,
        })),
      ])
      .flat();
    const usersWitoutGroup =
      overallScheduler.users?.filter(
        ({ id }) => !groupsData.some((item) => item.id === id),
      ) || [];

    return groupsData
      .concat([
        {
          type: 'group',
          id: 'no_group',
          label: t('common.withoutGroup'),
        },
        ...usersWitoutGroup.map((member) => ({
          type: 'user',
          id: member.id,
          group: 'no_group',
          label: getProfileName(member.profile),
          icon: member.profile.smallLogo || member.profile.logo || (
            <StyledAvatarNoPhoto>
              {getProfileName(member.profile)[0].toUpperCase()}
            </StyledAvatarNoPhoto>
          ),
          firstName: member.profile.firstName,
          lastName: member.profile.lastName,
          profileName: member.profile.profileName,
          email: member.profile.email,
          user: member,
        })),
      ])
      .flat();
  };

  const [usersData, setUsersData] = useState([
    ...getGroupsData(overallScheduler),
  ]);

  useEffect(() => {
    setUsersData([...getGroupsData(overallScheduler)]);
  }, [overallScheduler]);

  return usersData;
};

export const useGrid = (data, filterValue, communityId) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userId: currentUserId } = useSelector(getAuthUser);
  const { addToast } = useToasts();
  const [groups, setGroups] = useState(
    reduce(
      (acc, item) => {
        if (item.type === 'group') {
          acc[item.id] = {
            ...item,
            isShow: true,
          };
        }
        return acc;
      },
      {},
      data,
    ),
  );

  useEffect(() => {
    setGroups((prev) => syncGridGroupsState(prev, data, () => true));
  }, [data]);

  const filteredData = useMemo(
    () =>
      filter((item) => {
        if (item.type !== 'group') {
          return (
            groups[item.group]?.isShow &&
            (filterValue
              ? includes(
                  filterValue.toLowerCase(),
                  getProfileName(item)?.toLowerCase(),
                )
              : true)
          );
        }
        return true;
      }, data),
    [data, groups, filterValue],
  );

  const rowRenderer = useCallback(
    ({ index, key, style }) => {
      const handleSendingMessage = (e, userId) => {
        e.stopPropagation();
        if (currentUserId === userId) {
          addToast(t('errors.cantSendMessageToYourself'), {
            appearance: 'error',
          });
          return;
        }
        e.preventDefault();
        navigate(
          `${ROUTES.COMMUNITY}/${communityId}/${COMMUNITY_ROUTES.CHAT}?peerId=${userId}`,
        );
      };

      if (filteredData[index].type === 'group') {
        return (
          <div key={key} style={style}>
            <StyledGroupItem className="grid-group">
              <span
                data-clickable="true"
                data-type="group"
                data-id={filteredData[index].id}
                className={cn(
                  'grid-group-toogle-container',
                  'grid-clickable-item',
                  {
                    'grid-group-toogle-container-close':
                      !groups[filteredData[index].id]?.isShow,
                  },
                )}
              >
                <Down />
              </span>
              {filteredData[index].label}
            </StyledGroupItem>
          </div>
        );
      }

      return (
        <div
          key={key}
          style={style}
          className="grid-row"
          onClick={() =>
            dispatch(
              openModal({
                type: VIEW_MEMBER,
                position: 'right',
                data: filteredData[index].user,
              }),
            )
          }
        >
          <StyledListItem>
            <StyledNameWrapper>
              <ProfileName
                searchFilter={filterValue}
                name={getProfileName(filteredData[index])}
                avatar={filteredData[index].icon}
                communityPermission={
                  filteredData[index].permission?.communityPermission
                }
                groupPermission={
                  filteredData[index].permission?.groupPermission
                }
              />
            </StyledNameWrapper>
            <ChatBtnWrapper>
              <StyledLink
                onClick={(e) =>
                  handleSendingMessage(e, filteredData[index].user.id)
                }
                disabled={currentUserId === filteredData[index].user.id}
              >
                <StyledMessageBlock showBorder>
                  <IconSendMessage />
                  <StyledSendMessageLabel>
                    {t('common.sendMessage')}
                  </StyledSendMessageLabel>
                </StyledMessageBlock>
              </StyledLink>
            </ChatBtnWrapper>
          </StyledListItem>
        </div>
      );
    },
    [filteredData, groups],
  );

  // click event functions

  const toogleGroup = useCallback(
    (id) => {
      if (groups[id]) {
        setGroups(assoc([id, 'isShow'], !groups[id].isShow, groups));
      }
    },
    [groups],
  );

  const onClick = useCallback(
    (event) => {
      const elem = event.target.closest('[data-clickable="true"]');
      if (elem) {
        const { dataset } = elem;
        if (dataset.type === 'group') {
          toogleGroup(dataset.id);
        }
      }
    },
    [toogleGroup],
  );

  return {
    rowRenderer,
    onClick,
    filteredData,
  };
};
